import React, { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { LineChart as ChartIcon } from "lucide-react";

const dd = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
const when = (v) => new Date(v).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });

const useHistory = (key, days) => {
  const [h, setH] = useState(null);
  useEffect(() => {
    setH(null);
    api.get(`/admin/mobile-operators/${key}/credit-history`, { params: { days } }).then((r) => setH(r.data)).catch(() => setH({ series: [], entries: [] }));
  }, [key, days]);
  return h;
};

const Chart = ({ series, height, axes }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={series} margin={{ top: 4, right: 4, left: axes ? 8 : 0, bottom: 0 }}>
      {axes && <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />}
      {axes && <XAxis dataKey="day" tickFormatter={dd} fontSize={11} minTickGap={16} />}
      {axes && <YAxis fontSize={11} width={80} tickFormatter={(v) => euro(v)} />}
      <Tooltip formatter={(v) => (v == null ? "—" : euro(v))} labelFormatter={dd} />
      <Area type="stepAfter" dataKey="balance" name="Credito" stroke="#10b981" fill="#10b98133" strokeWidth={2} isAnimationActive={false} />
    </AreaChart>
  </ResponsiveContainer>
);

const Forecast = ({ h, testid }) => (
  <p className="text-xs text-muted-foreground" data-testid={testid}>
    {h.avg_daily_use > 0 ? <>Consumo medio {euro(h.avg_daily_use)} al giorno{h.days_left != null && <> · basta per circa <strong className={h.days_left < 7 ? "text-amber-600" : "text-foreground"}>{h.days_left} giorni</strong></>}</> : "Consumo medio non ancora calcolabile"}
  </p>
);

const Stat = ({ label, value, testid }) => <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">{label}</p><p className="text-lg font-semibold" data-testid={testid}>{value}</p></div>;

const HistoryDialog = ({ op, onClose }) => {
  const [days, setDays] = useState(30);
  const h = useHistory(op.key, days);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="op-credit-history-dialog">
        <DialogHeader><DialogTitle>Credito {op.name} giorno per giorno</DialogTitle></DialogHeader>
        <div className="flex gap-2">{[7, 30, 90].map((d) => <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)} data-testid={`op-credit-days-${d}`}>{d} giorni</Button>)}</div>
        {!h ? <p className="text-sm text-muted-foreground">Caricamento…</p> : <>
          <div className="h-64" data-testid="op-credit-history-chart"><Chart series={h.series} height={256} axes /></div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Stat label="Consumato nel periodo" value={euro(h.consumed || 0)} testid="op-credit-consumed" />
            <Stat label="Ricariche nel periodo" value={euro(h.recharged || 0)} testid="op-credit-recharged" />
            <Stat label="Previsione" value={h.days_left == null ? "—" : `circa ${h.days_left} giorni`} testid="op-credit-days-left" />
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-border text-sm" data-testid="op-credit-entries">
            {!h.entries.length && <p className="py-3 text-muted-foreground">Nessun aggiornamento nel periodo.</p>}
            {h.entries.map((e, i) => <div key={i} className="flex justify-between gap-3 py-2"><span>{when(e.at)} · {e.source === "operatore" ? "dall'operatore" : "a mano"}</span><strong>{euro(e.balance)}</strong></div>)}
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
};

export const CreditSpark = ({ op }) => {
  const [open, setOpen] = useState(false);
  const h = useHistory(op.key, 30);
  if (!h) return <div className="h-12" />;
  return (
    <div className="space-y-1.5" data-testid={`op-credit-history-${op.key}`}>
      {h.series.some((p) => p.balance != null) ? <div className="h-12"><Chart series={h.series} height={48} /></div> : <p className="text-xs text-muted-foreground">Nessuno storico negli ultimi 30 giorni</p>}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Forecast h={h} testid={`op-credit-forecast-${op.key}`} />
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => setOpen(true)} data-testid={`op-credit-history-open-${op.key}`}><ChartIcon className="w-3.5 h-3.5" />Storico</Button>
      </div>
      {open && <HistoryDialog op={op} onClose={() => setOpen(false)} />}
    </div>
  );
};
